import { Box } from '@mui/material';
import { Control, useFormState, useWatch } from 'react-hook-form';

import { TextInput } from '@/components/Form/TextInput';

import { useTranslate } from '@/hooks/useTranslate';

import {
  CharactersLeft,
  CurrencyLabel,
  FieldLabel,
  FinanceBlock,
} from '../common';
import { AssetFormValues } from './type';

const MAX_TYPE_LENGTH = 30;
const MAX_VALUE_DIGITS = 12;

type AssetBlockProps = {
  index: number;
  total: number;
  currencyDisplay: string;
  control: Control<AssetFormValues>;
  onDelete: () => void;
};

export function AssetBlock({
  index,
  total,
  currencyDisplay,
  control,
  onDelete,
}: Readonly<AssetBlockProps>) {
  const { t } = useTranslate();

  const { errors } = useFormState({
    control,
    name: [`assets.${index}.assetType`, `assets.${index}.amount`],
  });
  const assetTypeValue =
    useWatch({ control, name: `assets.${index}.assetType` }) ?? '';

  return (
    <FinanceBlock
      title={t('moreInfo.form.yourFinances.assets.assetBlockTitle')}
      counter={`${index + 1}/${total}`}
      isDeletable={total > 1}
      onDelete={onDelete}
    >
      <Box mb={2}>
        <FieldLabel>
          {t('moreInfo.form.yourFinances.assets.assetTypeLabel')}
        </FieldLabel>
        <TextInput<AssetFormValues>
          control={control}
          name={`assets.${index}.assetType`}
          error={!!errors.assets?.[index]?.assetType}
          helperText={errors.assets?.[index]?.assetType?.message}
          placeholder={t(
            'moreInfo.form.yourFinances.assets.assetTypePlaceholder',
          )}
          fullWidth
          size="small"
          inputProps={{ maxLength: MAX_TYPE_LENGTH }}
        />
        <CharactersLeft count={MAX_TYPE_LENGTH - assetTypeValue.length} />
      </Box>

      <Box>
        <FieldLabel>
          {t('moreInfo.form.yourFinances.assets.valueLabel')}
        </FieldLabel>
        <TextInput<AssetFormValues>
          control={control}
          name={`assets.${index}.amount`}
          error={!!errors.assets?.[index]?.amount}
          helperText={errors.assets?.[index]?.amount?.message}
          placeholder={t('moreInfo.form.yourFinances.assets.valuePlaceholder')}
          inputMode="numeric"
          fullWidth
          size="small"
          template="currency"
          startAdornment={<CurrencyLabel>{currencyDisplay}</CurrencyLabel>}
          inputProps={{ maxLength: MAX_VALUE_DIGITS }}
        />
      </Box>
    </FinanceBlock>
  );
}
